import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ExternalLink, CalendarClock, CalendarPlus, Smartphone, Globe, Trash2, AlertTriangle, Loader2, Wrench, Lock, LockOpen, RotateCcw, ChevronDown, LogIn, ShieldCheck, ShieldAlert, ShieldQuestion, PackagePlus, Mail, Search, ShieldBan } from "lucide-react";
import { Slider } from "@/components/ui/slider";

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function SuperTenants({ restricted = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [health, setHealth] = useState({}); // { [tenantId]: { loading, data } }
  const load = () => api.get("/platform/tenants")
    .then(({ data }) => setItems(Array.isArray(data) ? data : []))
    .catch((e) => toast.error(extractErr(e)))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const checkHealth = async (t) => {
    if (!t.custom_domain) return;
    setHealth((h) => ({ ...h, [t.id]: { loading: true, data: h[t.id]?.data } }));
    try {
      const { data } = await api.get(`/platform/tenants/${t.id}/domain-health`);
      setHealth((h) => ({ ...h, [t.id]: { loading: false, data } }));
    } catch {
      setHealth((h) => ({ ...h, [t.id]: { loading: false, data: { status: "error" } } }));
    }
  };

  // Auto-probe domain health for tenants that have a custom domain configured.
  useEffect(() => {
    items.forEach((t) => { if (t.custom_domain && !health[t.id]) checkHealth(t); });
  }, [items]);

  const toggle = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    try { await api.put(`/platform/tenants/${t.id}/status`, { status: next }); toast.success("Updated"); load(); } catch (e) { toast.error(extractErr(e)); }
  };

  const [loginAs, setLoginAs] = useState(null); // tenant id currently signing into

  const loginToTenant = async (t) => {
    const ok = window.confirm(
      restricted
        ? `Open ${t.name}'s dashboard?\n\nYou'll be signed in as this store's owner. You will need to sign back into Sales Panel afterwards.`
        : `Open ${t.name}'s dashboard?\n\nYou'll be signed in as this store's owner. You will need to sign back into Platform Admin afterwards.`
    );
    if (!ok) return;
    setLoginAs(t.id);
    try {
      await api.post(`/platform/tenants/${t.id}/login-as`);
      window.location.assign("/admin");
    } catch (e) {
      toast.error(extractErr(e));
      setLoginAs(null);
    }
  };

  const setAppBuilder = async (t, action) => {
    try {
      await api.put(`/platform/tenants/${t.id}/app-builder`, { action });
      toast.success(
        action === "unlock" ? "App builder unlocked for this tenant" :
        action === "lock" ? "App builder locked (over-building stopped)" :
        "App builder reset to normal (72h cooldown)"
      );
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const [quotaTarget, setQuotaTarget] = useState(null);
  const [quotaExtra, setQuotaExtra] = useState(0);
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailValue, setEmailValue] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const openQuota = (t) => {
    setQuotaTarget(t);
    setQuotaExtra(Number(t.extra_product_quota) || 0);
  };
  const saveQuota = async () => {
    if (!quotaTarget) return;
    setQuotaSaving(true);
    try {
      await api.put(`/platform/tenants/${quotaTarget.id}/product-quota`, { extra: quotaExtra });
      toast.success(`Product limit set to ${100 + quotaExtra} for ${quotaTarget.name}`);
      setQuotaTarget(null);
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setQuotaSaving(false); }
  };
  const openEmail = (t) => {
    setEmailTarget(t);
    setEmailValue(t.owner_email || "");
  };
  const saveEmail = async () => {
    if (!emailTarget) return;
    const email = emailValue.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setEmailSaving(true);
    try {
      await api.put(`/platform/tenants/${emailTarget.id}/email`, { email });
      toast.success(`Login email updated for ${emailTarget.name}`);
      setEmailTarget(null);
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setEmailSaving(false); }
  };
  const openDelete = (t) => { setDelTarget(t); setConfirmText(""); };
  const setDeletionLock = async (t, locked) => {
    try {
      await api.put(`/platform/tenants/${t.id}/deletion-lock`, { locked });
      toast.success(locked ? "Sales users cannot delete this store" : "Sales users can delete this store");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };
  const confirmDelete = async () => {
    if (confirmText.trim() !== "Confirm Delete") return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/platform/tenants/${delTarget.id}`, { data: { confirm: confirmText.trim() } });
      toast.success(`Deleted "${delTarget.name}" · ${data.files_removed || 0} files removed`);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setDeleting(false); }
  };

  const visible = items.filter((t) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [t.name, t.slug, t.owner_email, t.custom_domain].some((v) => String(v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100">{restricted ? "Clients" : "Tenants"}</h1>
          <p className="text-slate-400 text-sm mt-1">
            {restricted
              ? "Open a store dashboard or remove unused stores. Protected stores cannot be deleted."
              : "Manage merchant stores, protection, and access."}
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, slug or email"
            className="pl-9 bg-slate-900 border-slate-700 text-slate-100"
            data-testid="tenant-search"
          />
        </div>
      </div>
      <div className="text-xs text-slate-500" data-testid="tenant-count">
        {loading ? "Loading stores…" : `${visible.length} store${visible.length === 1 ? "" : "s"}`}
      </div>
      {loading ? (
        <div className="grid gap-3" data-testid="tenants-loading" aria-busy="true" aria-live="polite">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="bg-slate-900 border-slate-800 p-4 sm:p-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-800 animate-pulse shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-3.5 rounded bg-slate-800 animate-pulse" style={{ width: `${42 + i * 8}%`, maxWidth: "14rem" }} />
                  <div className="h-3 w-24 rounded bg-slate-800/70 animate-pulse" />
                </div>
                {i === 0 ? (
                  <div className="inline-flex items-center gap-2 text-sm text-indigo-200 shrink-0">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Loading stores</span>
                  </div>
                ) : (
                  <div className="hidden sm:block h-6 w-24 rounded-full bg-slate-800 animate-pulse" />
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-8 text-center text-slate-500 text-sm">No tenants yet.</Card>
      ) : visible.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-8 text-center text-slate-500 text-sm">No tenants match.</Card>
      ) : (
          <div className="grid gap-3">
            {visible.map((t) => {
              const sub = t.subscription || {};
              const isApp = sub.plan_id === "website_app" || sub.plan_id === "website_app_ios";
              const expired = sub.expired;
              return (
                <Card key={t.id} className="bg-slate-900 border-slate-800 p-4 sm:p-5 text-slate-100" data-testid={`tenant-row-${t.slug}`}>
                  <div className="flex flex-col xl:flex-row xl:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-slate-200 shrink-0">
                        {(t.name || t.slug || "S").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-slate-100 truncate">{t.name}</div>
                        <Link to={`/${t.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-mono-nums text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1">/{t.slug} <ExternalLink className="w-3 h-3" /></Link>
                        {t.owner_email ? (
                          <div className="text-xs text-slate-400 truncate mt-0.5" data-testid={`tenant-email-${t.slug}`}>{t.owner_email}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Plan */}
                  <div className="w-full xl:w-40 flex flex-wrap gap-2">
                    <Badge className={isApp ? "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/30 gap-1" : "bg-sky-500/15 text-sky-200 border-sky-500/30 gap-1"} data-testid={`tenant-plan-${t.slug}`}>
                      {isApp ? <Smartphone className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                      {isApp ? "Website + App" : "Website"}
                    </Badge>
                    <Badge className={t.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}>{t.status}</Badge>
                    {t.deletion_locked ? (
                      <Badge className="bg-amber-500/15 text-amber-200 border-amber-500/30 gap-1" data-testid={`tenant-protected-${t.slug}`}><ShieldBan className="w-3 h-3" /> Protected</Badge>
                    ) : null}
                  </div>

                  {/* Joined + Expiry */}
                  <div className="w-full xl:w-64 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold flex items-center gap-1"><CalendarPlus className="w-3 h-3" /> Joined</div>
                      <div className="text-slate-200 font-mono-nums" data-testid={`tenant-joined-${t.slug}`}>{fmtDate(sub.created_at || t.created_at)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Expires</div>
                      <div className={`font-mono-nums ${expired ? "text-red-300" : "text-slate-200"}`} data-testid={`tenant-expiry-${t.slug}`}>
                        {fmtDate(sub.expires_at)}
                        {expired && <span className="ml-1 text-red-400">(expired)</span>}
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-slate-400 font-mono-nums w-full xl:w-auto">
                    <button
                      type="button"
                      onClick={() => openQuota(t)}
                      className="text-left hover:text-indigo-200"
                      data-testid={`tenant-quota-${t.slug}`}
                      title="Increase product posting limit"
                    >
                      {t.counts.products}/{t.product_limit || 100} products
                    </button>
                    <span> · {t.counts.orders} orders · {t.counts.customers} customers</span>
                  </div>

                  {/* Custom-domain health */}
                  <div className="w-full xl:w-52" data-testid={`tenant-domain-${t.slug}`}>
                    {!t.custom_domain ? (
                      <Badge className="bg-slate-700/30 text-slate-400 border-slate-600/40 gap-1"><Globe className="w-3 h-3" /> No custom domain</Badge>
                    ) : (() => {
                      const hs = health[t.id];
                      const d = hs?.data || {};
                      const loading = hs?.loading;
                      let cls = "bg-slate-600/20 text-slate-300 border-slate-600/40";
                      let Icon = ShieldQuestion;
                      let label = "Unknown";
                      if (loading) { Icon = Loader2; label = "Checking…"; }
                      else if (d.status === "secured") { cls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"; Icon = ShieldCheck; label = "Connected & secured"; }
                      else if (d.status === "connected") { cls = "bg-amber-500/15 text-amber-300 border-amber-500/30"; Icon = ShieldAlert; label = "DNS ok · HTTPS pending"; }
                      else if (d.status === "misconfigured") { cls = "bg-red-500/15 text-red-300 border-red-500/30"; Icon = ShieldAlert; label = "DNS not pointing here"; }
                      else if (d.status === "error") { cls = "bg-red-500/15 text-red-300 border-red-500/30"; Icon = ShieldAlert; label = "Check failed"; }
                      const tip = d.custom_domain
                        ? `${d.custom_domain}\nDNS: ${d.dns_ok ? "OK" : "mismatch"} (${(d.resolved_ips||[]).join(", ") || "no A record"} → expected ${d.expected_ip})\nHTTPS: ${d.https_ok ? "live" : "not serving"}\nClick to re-check`
                        : "Click to check";
                      return (
                        <button
                          type="button"
                          onClick={() => checkHealth(t)}
                          disabled={loading}
                          title={tip}
                          data-testid={`tenant-domain-badge-${t.slug}`}
                          className="text-left w-full focus:outline-none"
                        >
                          <div className="text-[11px] font-mono-nums text-slate-400 truncate">{t.custom_domain}</div>
                          <Badge className={`${cls} gap-1 mt-0.5`}>
                            <Icon className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> {label}
                          </Badge>
                        </button>
                      );
                    })()}
                  </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-2 sm:gap-3 flex-wrap">
                    {(() => {
                      const ov = t.app_builder_override;
                      const badge = ov === "unlock"
                        ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1"><LockOpen className="w-3 h-3" />Builder: unlocked</Badge>
                        : ov === "lock"
                        ? <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/30 gap-1"><Lock className="w-3 h-3" />Builder: locked</Badge>
                        : <Badge className="bg-slate-600/20 text-slate-300 border-slate-600/40 gap-1"><Wrench className="w-3 h-3" />Builder: auto</Badge>;
                      return (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex items-center gap-1 focus:outline-none" data-testid={`tenant-appbuilder-trigger-${t.slug}`}>
                              {badge}<ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-72">
                            <DropdownMenuLabel>App builder control</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="cursor-pointer" onClick={() => setAppBuilder(t, "unlock")} data-testid={`tenant-appbuilder-unlock-${t.slug}`}>
                              <LockOpen className="w-4 h-4 mr-2 text-emerald-600" /> Paid offline — unlock app builder
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer" onClick={() => setAppBuilder(t, "lock")} data-testid={`tenant-appbuilder-lock-${t.slug}`}>
                              <Lock className="w-4 h-4 mr-2 text-orange-600" /> App is now live — stop overbuilding
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer" onClick={() => setAppBuilder(t, "auto")} data-testid={`tenant-appbuilder-auto-${t.slug}`}>
                              <RotateCcw className="w-4 h-4 mr-2 text-slate-500" /> Reset to normal (72h cooldown)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()}
                    <Button variant="outline" size="sm" onClick={() => loginToTenant(t)} disabled={loginAs === t.id} data-testid={`tenant-login-as-${t.slug}`} className="border-indigo-700 bg-indigo-950/40 hover:bg-indigo-900/60 text-indigo-200 w-full sm:w-auto">
                      {loginAs === t.id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LogIn className="w-4 h-4 mr-1.5" />}Open dashboard
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEmail(t)} data-testid={`tenant-email-btn-${t.slug}`} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100" title="Change merchant login email">
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openQuota(t)} data-testid={`tenant-quota-btn-${t.slug}`} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100" title="Increase product limit">
                      <PackagePlus className="w-4 h-4" />
                    </Button>
                    {!restricted && (
                      <>
                    <Button variant="outline" size="sm" onClick={() => toggle(t)} data-testid={`tenant-toggle-${t.slug}`} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">{t.status === "active" ? "Suspend" : "Activate"}</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletionLock(t, !t.deletion_locked)}
                      data-testid={`tenant-deletion-lock-${t.slug}`}
                      className={t.deletion_locked ? "border-amber-700 bg-amber-950/40 hover:bg-amber-900/50 text-amber-200" : "border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100"}
                      title="When on, sales users cannot delete this store"
                    >
                      <ShieldBan className="w-4 h-4 mr-1.5" />{t.deletion_locked ? "Allow delete" : "Don't let anyone delete"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDelete(t)} data-testid={`tenant-delete-${t.slug}`} className="border-red-800 bg-red-950/40 hover:bg-red-900/60 text-red-300" title="Delete tenant forever"><Trash2 className="w-4 h-4" /></Button>
                      </>
                    )}
                    {restricted && !t.deletion_locked && (
                      <Button variant="outline" size="sm" onClick={() => openDelete(t)} data-testid={`tenant-delete-${t.slug}`} className="border-red-800 bg-red-950/40 hover:bg-red-900/60 text-red-300" title="Delete this store"><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button>
                    )}
                    {restricted && t.deletion_locked && (
                      <Button variant="outline" size="sm" disabled data-testid={`tenant-delete-locked-${t.slug}`} className="border-slate-800 bg-slate-900 text-slate-500 cursor-not-allowed" title="Super admin protected this store">
                        <ShieldBan className="w-4 h-4 mr-1.5" />Can't delete
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

      {emailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="tenant-email-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-100">Change login email</h2>
                <p className="text-xs text-slate-400 mt-1">
                  {emailTarget.name} · /{emailTarget.slug}. The merchant will sign in to the dashboard with this email.
                </p>
              </div>
              <div>
                <label className="text-xs text-slate-400">Account email</label>
                <Input
                  autoFocus
                  type="email"
                  data-testid="tenant-email-input"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="owner@example.com"
                  className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEmailTarget(null)} disabled={emailSaving} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">Cancel</Button>
                <Button onClick={saveEmail} disabled={emailSaving} data-testid="tenant-email-save" className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {emailSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save email"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {quotaTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="tenant-quota-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-100">Increase product limit</h2>
                <p className="text-xs text-slate-400 mt-1">
                  {quotaTarget.name} · using {quotaTarget.counts?.products || 0} of {100 + quotaExtra} slots
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm text-slate-300 mb-2">
                  <span>Extra slots (beyond 100)</span>
                  <span className="font-mono-nums text-indigo-200" data-testid="tenant-quota-extra-val">+{quotaExtra}</span>
                </div>
                <Slider
                  min={0}
                  max={400}
                  step={50}
                  value={[quotaExtra]}
                  onValueChange={(v) => setQuotaExtra(v[0])}
                  data-testid="tenant-quota-slider"
                />
                <div className="mt-2 flex justify-between text-[11px] text-slate-500 font-mono-nums">
                  <span>+0 → 100 total</span>
                  <span>+400 → 500 total</span>
                </div>
              </div>
              <p className="text-sm text-slate-200">
                Base 100 + extra {quotaExtra} = <b className="text-white font-mono-nums" data-testid="tenant-quota-total">{100 + quotaExtra}</b> products allowed.
              </p>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setQuotaTarget(null)} disabled={quotaSaving} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">Cancel</Button>
                <Button onClick={saveQuota} disabled={quotaSaving} data-testid="tenant-quota-save" className="bg-indigo-500 hover:bg-indigo-600 text-white">
                  {quotaSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save limit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="tenant-delete-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-red-900/60 shadow-2xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
                <div>
                  <h2 className="font-display text-lg font-bold text-slate-100">Delete tenant forever</h2>
                  <p className="text-xs text-slate-400">This action cannot be undone.</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-300">
                You are about to permanently delete <b className="text-white">{delTarget.name}</b> <span className="font-mono-nums text-indigo-300">/{delTarget.slug}</span> including its store, products, orders, customers, pages, settings and all uploaded files.
              </p>
              <div className="mt-4">
                <label className="text-xs text-slate-400">Type <b className="text-red-300">Confirm Delete</b> to proceed</label>
                <Input
                  autoFocus
                  data-testid="tenant-delete-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Confirm Delete"
                  className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                />
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setDelTarget(null)} disabled={deleting} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">Cancel</Button>
                <Button onClick={confirmDelete} disabled={deleting || confirmText.trim() !== "Confirm Delete"} data-testid="tenant-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                  {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>) : (<><Trash2 className="w-4 h-4 mr-2" /> Delete forever</>)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
